import {
  computeSection,
  type TemplateDoc,
  TemplateDoc as TemplateDocSchema,
} from '@quickreport/domain';
import { Badge, Button, Modal } from '@quickreport/ui';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Eye, Redo2, Save, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '../../lib/workspace';
import { loadTemplate, saveTemplateVersion } from '../../local-db/templates';
import { GridEditor } from './GridEditor';
import { PreviewPane } from './PreviewPane';
import { RowPanel } from './RowPanel';
import { TemplatePanel } from './TemplatePanel';
import { useHistory } from './useHistory';

// Flags in the grid use a typical adult. Sex- and age-specific ranges are checked in a real report.
const SAMPLE_PATIENT = { sex: 'M', ageDays: Math.round(35 * 365.25) } as const;

export function TemplateBuilderPage() {
  const { templateId } = useParams({ from: '/templates/$templateId' });
  const { db } = useWorkspace();
  const [loaded, setLoaded] = useState<{ doc: TemplateDoc; version: number } | null | undefined>(
    undefined,
  );

  useEffect(() => {
    let alive = true;
    loadTemplate(db, templateId).then((t) => alive && setLoaded(t));
    return () => {
      alive = false;
    };
  }, [db, templateId]);

  if (loaded === undefined) return <div className="p-8 text-slate">Opening template…</div>;
  if (loaded === null)
    return <div className="p-8">This template does not exist on this device.</div>;
  return (
    <Builder
      key={`${templateId}:${loaded.version}`}
      templateId={templateId}
      initial={loaded.doc}
      version={loaded.version}
    />
  );
}

function Builder({
  templateId,
  initial,
  version,
}: {
  templateId: string;
  initial: TemplateDoc;
  version: number;
}) {
  const { db, orgId } = useWorkspace();
  const history = useHistory(initial);
  const doc = history.value;
  const [savedDoc, setSavedDoc] = useState(initial);
  const [savedVersion, setSavedVersion] = useState(version);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [examples, setExamples] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);
  const selectRow = (id: string | null) => setSelectedRowId(id);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const dirty = doc !== savedDoc;

  const computed = useMemo(() => computeSection(doc, examples, SAMPLE_PATIENT), [doc, examples]);
  const selectedRow = doc.rows.find((r) => r.id === selectedRowId);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  async function save() {
    const parsed = TemplateDocSchema.safeParse(doc);
    if (!parsed.success) {
      setMessage({ tone: 'error', text: parsed.error.issues.map((i) => i.message).join(' · ') });
      return;
    }
    const res = await db.transaction((tx) =>
      saveTemplateVersion(tx, orgId, templateId, parsed.data),
    );
    setSavedDoc(doc);
    setSavedVersion(res.version);
    setMessage({
      tone: 'ok',
      text: `Saved as version ${res.version}. Reports already made keep the version they used.`,
    });
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-2 border-b border-line bg-paper px-4 py-2">
        <Link
          to="/templates"
          className="rounded p-1.5 text-slate hover:bg-canvas"
          aria-label="Back to templates"
        >
          <ArrowLeft size={18} />
        </Link>
        <input
          value={doc.name}
          onChange={(e) => history.set((d) => ({ ...d, name: e.target.value }))}
          aria-label="Template name"
          className="min-w-0 flex-1 rounded border border-transparent px-1.5 py-1 text-[17px] font-semibold hover:border-line focus:border-brand focus:outline-none"
        />
        <Badge>v{savedVersion}</Badge>
        {dirty ? <Badge tone="brand">Unsaved changes</Badge> : null}
        <div className="mx-1 h-6 w-px bg-line" />
        <Button
          tone="ghost"
          size="sm"
          onClick={history.undo}
          disabled={!history.canUndo}
          aria-label="Undo"
        >
          <Undo2 size={15} />
        </Button>
        <Button
          tone="ghost"
          size="sm"
          onClick={history.redo}
          disabled={!history.canRedo}
          aria-label="Redo"
        >
          <Redo2 size={15} />
        </Button>
        <Button size="sm" onClick={() => setShowPreview(true)}>
          <Eye size={15} /> Preview
        </Button>
      </header>

      {message ? (
        <div
          className={`border-b px-4 py-1.5 text-[13px] ${message.tone === 'ok' ? 'border-green-200 bg-green-50 text-ok' : 'border-red-200 bg-red-50 text-flag'}`}
          role={message.tone === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </div>
      ) : null}

      {/* One page: settings on top, the grid below. */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="border-b border-line bg-paper">
          {selectedRow?.kind === 'test' ? (
            <RowPanel
              doc={doc}
              row={selectedRow}
              onChange={history.set}
              onClose={() => setSelectedRowId(null)}
            />
          ) : (
            <TemplatePanel doc={doc} onChange={history.set} />
          )}
        </div>
        <div className="p-4">
          <GridEditor
            doc={doc}
            onChange={history.set}
            computed={computed}
            examples={examples}
            onExample={(id, v) => setExamples((e) => ({ ...e, [id]: v }))}
            selectedRowId={selectedRowId}
            onSelectRow={selectRow}
            onError={(text) => setMessage({ tone: 'error', text })}
          />
          <p className="mt-3 text-[12px] text-slate">
            Type example results in the Result column to check flags and formulas. Examples are not
            saved. Click a row to change its settings above. Drag the grip handles to move rows,
            columns, or free-text cells.
          </p>
        </div>
      </div>

      <footer className="flex items-center justify-end gap-3 border-t border-line bg-paper px-4 py-2.5">
        <span className="text-[13px] text-slate" aria-live="polite">
          {dirty ? 'You have unsaved changes.' : `Saved as version ${savedVersion}.`}
        </span>
        <Button tone="primary" onClick={save} disabled={!dirty}>
          <Save size={15} /> Save
        </Button>
      </footer>

      {showPreview ? (
        <Modal
          wide
          title={`Preview · ${doc.name}`}
          label="Template preview"
          onClose={() => setShowPreview(false)}
        >
          <div className="min-h-0 flex-1 overflow-auto rounded-md border border-line">
            <PreviewPane doc={doc} computed={computed} />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setShowPreview(false)}>Close</Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
